import React, { useEffect, useState, useRef } from 'react';
import { Wrapper } from '../style';
import { Button, Checkbox, ConfigProvider, Dropdown, Menu, message, Modal, Pagination, Table, Tag, Tooltip, Select, Card, Row, Col, Flex } from 'antd';
import { fakeProImage } from '../../../../config/fakeData';
import Type from './type';
import Category from './category';
import { Link } from 'react-router-dom';
import { ConvertParams, defaultPageSize } from '../../../../config';
import { MakeApiCall } from '../../../../apis';
import moment from 'moment';
import { useAlerts } from '../../../../contexts/AlertContext';
import { useThemeMode } from "../../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../../theme/antdTheme";
import { getAmount } from '../../../../utils/helper';

const READ_ROW_CLASS = 'alert-read-row';

// Custom hook to get window size
const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call immediately to set initial size

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

function AllAlerts({
                       userData,
                       selectedAsins = [],
                       selectedCategory = null,
                       selectedSeverity = null,
                       selectedDateRange = [],
                       selectedCurrencySign = '₹',
                       onCategoryChange,
                   }) {
    const { themeMode } = useThemeMode();

    const [activeTab, setActiveTab] = useState('category');

    const [totalPage, setTotalPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);

    // --- Selected row IDs for bulk action ---
    const [selectedIds, setSelectedIds] = useState([]);
    const [markingRead, setMarkingRead] = useState(false);
    const isMounted = useRef(true);

    // --- Modal state for mobile view ---
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    const [analyticsData, setAnalyticsData] = useState({
        total_alerts: 0,
        unread_count: 0,
        alerts_by_categories: [],
        alerts_by_type: [],
    });
    const [analyticsLoading, setAnalyticsLoading] = useState(true);

    const [filter, setFilter] = useState({
        page: 1,
        'per-page': defaultPageSize,
        search_text: '',
        alert_name: '',
        is_read: '0',
        asin: '',
        category: '',
        severity: '',
        start_date: '',
        end_date: '',
    });

    const { setUnreadCount } = useAlerts();
    
    // Get window size and determine if mobile
    const windowSize = useWindowSize();
    const isMobile = windowSize.width < 768;
    const isTablet = windowSize.width >= 768 && windowSize.width < 1024;

    const getProductList = async (currentFilter) => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (trialExpiredLocal) { setList([]); setLoading(false); return; }
        setLoading(true);
        const response = await MakeApiCall(
            `get-alert-notifications${ConvertParams(currentFilter)}`,
            'get',
            null,
            true,
            {},
            userData?.token
        );

        if (response?.status) {
            if (!isMounted.current) return;
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            const records = response?.data?.records || [];
            const recordsWithReadStatus = records.map(record => ({
                ...record,
                is_read: record.is_read === 1,
                key: record.id,
            }));
            setList(recordsWithReadStatus);
            setSelectedIds([]);
            setLoading(false);
        } else {
            if (!isMounted.current) return;
            message.warning(response?.message);
            setLoading(false);
            setList([]);
        }
    };

    // Accepts the same filter object so analytics always reflects current filter state
    const fetchAnalyticsData = async (currentFilter = {}) => {
        if (!isMounted.current) return;
        
        // Debug log to see what's being sent
        console.log('Fetching analytics with filter:', currentFilter);
        
        setAnalyticsLoading(true);

        // Build analytics params from the same filter fields used by the list endpoint
        const analyticsParams = {};
        
        // Only add parameters if they have meaningful values
        if (currentFilter.asin && currentFilter.asin.trim() !== '') {
            analyticsParams.asin = currentFilter.asin;
        }
        // if (currentFilter.category && currentFilter.category.trim() !== '') {
        //     analyticsParams.category = currentFilter.category;
        // }
        if (currentFilter.severity && currentFilter.severity.trim() !== '') {
            analyticsParams.severity = currentFilter.severity;
        }
        if (currentFilter.start_date && currentFilter.start_date.trim() !== '') {
            analyticsParams.start_date = currentFilter.start_date;
        }
        if (currentFilter.end_date && currentFilter.end_date.trim() !== '') {
            analyticsParams.end_date = currentFilter.end_date;
        }
        if (currentFilter.alert_name && currentFilter.alert_name.trim() !== '') {
            analyticsParams.alert_name = currentFilter.alert_name;
        }
        if (currentFilter.is_read !== null && currentFilter.is_read !== undefined && currentFilter.is_read !== '') {
            analyticsParams.is_read = currentFilter.is_read;
        }

        console.log('Analytics params being sent:', analyticsParams);

        try {
            const response = await MakeApiCall(
                `get-alert-notification-analytics${ConvertParams(analyticsParams)}`,
                'get',
                null,
                true,
                {},
                userData?.token
            );

            if (!isMounted.current) return;

            if (response?.status) {
                const data = {
                    total_alerts: response.data.total_alerts || 0,
                    unread_count: response.data.unread_count || 0,
                    alerts_by_categories: Array.isArray(response.data.alerts_by_categories)
                        ? response.data.alerts_by_categories
                        : [],
                    alerts_by_type: Array.isArray(response.data.alerts_by_type)
                        ? response.data.alerts_by_type
                        : [],
                };
                setAnalyticsData(data);
                setUnreadCount(data.unread_count);
            } else {
                message.warning(response?.message || 'Failed to load alert analytics.');
                setAnalyticsData({ total_alerts: 0, unread_count: 0, alerts_by_categories: [], alerts_by_type: [] });
                setUnreadCount(0);
            }
        } catch (error) {
            if (!isMounted.current) return;
            console.error('Error fetching alert analytics:', error);
            message.error('Error fetching alert analytics.');
            setAnalyticsData({ total_alerts: 0, unread_count: 0, alerts_by_categories: [], alerts_by_type: [] });
            setUnreadCount(0);
        } finally {
            if (isMounted.current) setAnalyticsLoading(false);
        }
    };

    // Track mounted state to avoid state updates after unmount
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Sync filters from parent dropdowns (asin, category, severity, date range)
    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            asin: Array.isArray(selectedAsins) && selectedAsins.length ? selectedAsins.join(',') : '',
            category: selectedCategory || '',
            severity: selectedSeverity || '',
            start_date: Array.isArray(selectedDateRange) && selectedDateRange[0]
                ? selectedDateRange[0].format('YYYY-MM-DD')
                : '',
            end_date: Array.isArray(selectedDateRange) && selectedDateRange[1]
                ? selectedDateRange[1].format('YYYY-MM-DD')
                : '',
            page: 1,
        }));
    }, [selectedAsins, selectedCategory, selectedSeverity, selectedDateRange]);

    // Fetch both list and analytics whenever filter changes
    useEffect(() => {
        if (userData?.token) {
            const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
            if (trialExpiredLocal) { 
                setLoading(false); 
                setList([]); 
                setAnalyticsData({ total_alerts: 0, unread_count: 0, alerts_by_categories: [], alerts_by_type: [] }); 
                return; 
            }
            
            // Fetch both list and analytics
            getProductList(filter);
            fetchAnalyticsData(filter);
        }
    }, [filter, userData?.token]);

    // --- Select All logic ---
    const allIds = list.map(item => item.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));
    const someSelected = allIds.some(id => selectedIds.includes(id)) && !allSelected;

    const handleSelectAll = (e) => {
        setSelectedIds(e.target.checked ? allIds : []);
    };

    const handleSelectRow = (id, checked) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(i => i !== id));
    };

    // --- Bulk Mark as Read ---
    const handleBulkMarkAsRead = async () => {
        if (selectedIds.length === 0) {
            message.warning('Please select at least one alert.');
            return;
        }

        setMarkingRead(true);
        try {
            const payload = allSelected ? { mark_all: true } : { ids: selectedIds };
            const response = await MakeApiCall(
                'mark-as-read-alerts-bulk',
                'post',
                payload,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                message.success('Selected alerts marked as read.');
                if (!isMounted.current) return;
                const markedCount = list.filter(item => selectedIds.includes(item.id) && !item.is_read).length;
                setUnreadCount(prev => Math.max(0, prev - markedCount));
                await getProductList(filter);
                // Refresh analytics with current filter after bulk action
                if (isMounted.current) fetchAnalyticsData(filter);
            } else {
                if (!isMounted.current) return;
                message.error(response?.message || 'Failed to mark alerts as read.');
            }
        } catch (error) {
            message.error('Something went wrong. Please try again.');
        } finally {
            if (isMounted.current) setMarkingRead(false);
        }
    };

    const handleAlertTypeFilter = (alertType) => {
        setFilter(prevFilter => ({
            ...prevFilter,
            alert_name: alertType,
            page: 1,
        }));
    };

    const bannerHeight = userData?.user?.user_data?.trial_expires_at ? 40 : 0;

    const getSeverityColor = (severity) => {
        switch(severity) {
            case 'Major':
                return '#ff4d4f';
            case 'Minor':
                return '#52c41a';
            default:
                return '#d9d9d9';
        }
    };

    // Render detail modal for mobile view
    const renderDetailModal = () => {
        if (!selectedRecord) return null;
        
        return (
            <Modal
                title="Alert Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={isMobile ? '97%' : 600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
                footer={false}
            >
                <div className="alert-detail-modal">
                    {/* Product Image and Basic Info */}
                    <div className="d-flex align-items-center mb-4">
                        <div className="symbol symbol-80px overflow-hidden me-3">
                            <img 
                                src={selectedRecord?.item_image || fakeProImage} 
                                alt="" 
                                style={{ width: '80px', height: '80px', objectFit: 'cover' }} 
                            />
                        </div>
                        <div className="flex-grow-1">
                            <div className="fw-bolder text-dark fs-5 mb-2">{selectedRecord?.item_name || ''}</div>
                            <div className="fs-7 text-muted">
                                <span><b>SKU:</b> {selectedRecord?.sku}</span> / <span><b>ASIN:</b> {selectedRecord?.asin}</span>
                            </div>
                            <div className="fs-7 text-muted mt-1">
                                <b>{getAmount(selectedRecord?.item_price, selectedCurrencySign)}</b>
                            </div>
                        </div>
                    </div>

                    {/* All Details */}
                    <div className="detail-section">
                        <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">{selectedRecord.category || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Alert Name:</span>
                            <span className="detail-value">{selectedRecord.alert_name || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Description:</span>
                            <span className="detail-value">{selectedRecord.description || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Comment:</span>
                            <span className="detail-value">
                                {selectedRecord.comment ? (
                                    selectedRecord.comment
                                ) : (
                                    <a
                                        onClick={() => {
                                            setModalVisible(false);
                                            Modal.info({
                                                title: 'Add Comment',
                                                content: 'Comment modal content here...',
                                            });
                                        }}
                                        className="text-danger fw-semibold"
                                    >
                                        Add Comment
                                    </a>
                                )}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Date & Time:</span>
                            <span className="detail-value">
                                {selectedRecord.alert_time 
                                    ? moment.unix(selectedRecord.alert_time).format('YYYY-MM-DD HH:mm:ss')
                                    : '-'}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Marketplace:</span>
                            <span className="detail-value d-flex align-items-center">
                                {selectedRecord.flag && <img src={selectedRecord.flag} alt="" width={20} className="me-2" />}
                                <span>{selectedRecord.marketplace || '-'}</span>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Severity:</span>
                            <span className="detail-value">
                                {selectedRecord.severity ? (
                                    <Tag color={selectedRecord.severity === 'Major' ? 'volcano' : 'green'}>
                                        {selectedRecord.severity}
                                    </Tag>
                                ) : '-'}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                                <Tag color={selectedRecord.is_read ? 'green' : 'orange'}>
                                    {selectedRecord.is_read ? 'Read' : 'Unread'}
                                </Tag>
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    // Render card view for mobile
    const renderCardView = () => {
        if (loading) {
            return <div className="text-center py-5">Loading...</div>;
        }

        if (list.length === 0) {
            return <div className="text-center py-5">No alerts found</div>;
        }

        return (
            <div className="alert-cards-container">
                {list.map((record) => (
                    <Card 
                        key={record.id}
                        className={`mb-3 ${record.is_read ? READ_ROW_CLASS : ''}`}
                        style={{ 
                            borderRadius: '8px',
                            backgroundColor: record.is_read ? '#f5f5f5' : '#fff'
                        }}
                    >
                        <div className="card-content">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <Checkbox
                                    checked={selectedIds.includes(record.id)}
                                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                                />
                                <div>
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            setSelectedRecord(record);
                                            setModalVisible(true);
                                        }}
                                        icon={<i className="ki-solid ki-eye fs-3"></i>}
                                        className="me-1"
                                    />
                                    <Dropdown overlay={menu(record)} trigger={['click']}>
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<i className="ki-solid ki-dots-vertical fs-3"></i>}
                                        />
                                    </Dropdown>
                                </div>
                            </div>
                            
                            {/* Main Fields - Always Visible */}
                            <div className="d-flex align-items-center mb-3">
                                <div className="symbol symbol-50px overflow-hidden me-3">
                                    <img src={record?.item_image || fakeProImage} alt="" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                </div>
                                <div className="flex-grow-1">
                                    <div
                                        className="fw-bolder text-dark mb-1 text-one"
                                        style={isMobile ? { width: '220px', wordBreak: 'break-word' } : {}}
                                    >
                                        {record?.item_name || ''}
                                    </div>
                                    <div className="fs-7 text-muted ">
                                        <Flex><b>SKU:</b> {record?.sku}</Flex> <Flex><b>ASIN:</b> {record?.asin}</Flex>
                                    </div>
                                    <div className="fs-7 text-muted">
                                        <b>{getAmount(record?.item_price, selectedCurrencySign)}</b>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Key Fields - 5 Main Fields */}
                            <Row gutter={[8, 8]} className="mb-2">
                                <Col span={12}>
                                    <div className="fs-7">
                                        <div className="text-muted">Category:</div>
                                        <span className="ms-0 fw-semibold">{record.category || '-'}</span>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className="fs-7">
                                        <div className="text-muted">Alert Name:</div>
                                        <span className="ms-0 fw-semibold">{record.alert_name || '-'}</span>
                                    </div>
                                </Col>
                            </Row>
                            
                            <div className="mb-2">
                                <div className="fs-7">
                                    <span className="text-muted">Severity:</span>
                                    <span className="ms-2">
                                        {record.severity ? (
                                            <Tag color={record.severity === 'Major' ? 'volcano' : 'green'}>{record.severity}</Tag>
                                        ) : '-'}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="mb-2">
                                <div className="fs-7">
                                    <span className="text-muted">Date & Time:</span>
                                    <span className="ms-2">
                                        {record.alert_time 
                                            ? moment.unix(record.alert_time).format('YYYY-MM-DD HH:mm')
                                            : '-'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* View Details Button */}
                            <Button 
                                type="link" 
                                className="p-0 mt-2"
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setModalVisible(true);
                                }}
                            >
                                View all details <i className="ki-outline ki-arrow-right fs-4 ms-1"></i>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    const columns = [
        {
            title: (
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                />
            ),
            key: 'select',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    checked={selectedIds.includes(record.id)}
                    onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                />
            ),
        },
        {
            title: 'Product Details',
            align: 'center',
            dataIndex: 'productName',
            width: 350,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.item_image || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a href="#" className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px">
                            {record?.item_name || ''}
                        </a>
                        <div className="fs-7 mt-1">
                            <span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span>
                        </div>
                        <div className="fs-7 mt-1">
                            <span><b>{getAmount(record?.item_price, selectedCurrencySign)}</b></span>
                        </div>
                    </div>
                </div>
            ),
            sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || ''),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            align: 'center',
            width: 120,
            sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
        },
        {
            title: 'Alert Name',
            dataIndex: 'alert_name',
            align: 'center',
            width: 150,
            sorter: (a, b) => (a.alert_name || '').localeCompare(b.alert_name || ''),
        },
        {
            title: 'Description',
            align: 'center',
            dataIndex: 'description',
            width: 250,
            render: (text) => (
                <div>
                    <Tooltip placement="top" title={text}>
                        <span className="text-two">{text}</span>
                    </Tooltip>
                </div>
            ),
            sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        },
        {
            title: 'Comment',
            dataIndex: 'comment',
            align: 'center',
            render: (comment) => {
                if (comment) {
                    return <span>{comment}</span>;
                }
                return (
                    <a
                        onClick={() =>
                            Modal.info({
                                title: 'Add Comment',
                                content: 'Comment modal content here...',
                            })
                        }
                        style={{ color: 'red', fontWeight: 600 }}
                    >
                        Add Comment
                    </a>
                );
            },
            sorter: (a, b) => (a.comment || '').localeCompare(b.comment || ''),
        },
        {
            title: 'Alert Date & Time',
            dataIndex: 'alert_time',
            align: 'center',
            width: 180,
            render: (text) => (
                text
                    ? <span>{moment.unix(text).format('YYYY-MM-DD HH:mm:ss')}</span>
                    : <span>-</span>
            ),
            sorter: (a, b) => a.alert_time - b.alert_time,
        },
        {
            title: 'Marketplace',
            align: 'center',
            width: 140,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {record.flag && <img src={record.flag} alt="" width={20} />}
                    <span style={{ fontWeight: 600 }}>{record.marketplace || '-'}</span>
                </div>
            ),
            sorter: (a, b) => (a.marketplace || '').localeCompare(b.marketplace || ''),
        },
        {
            title: 'Severity',
            dataIndex: 'severity',
            align: 'center',
            width: 110,
            render: (severity) => severity ? (
                <Tag color={severity === 'Major' ? 'volcano' : 'green'}>{severity}</Tag>
            ) : '-',
            sorter: (a, b) => (a.severity || '').localeCompare(b.severity || ''),
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 80,
            render: (_, row) => (
                <Dropdown overlay={menu(row)} trigger={['click']}>
                    <Button
                        type="link"
                        data-kt-menu-trigger="click"
                        data-kt-menu-placement="bottom-end"
                        data-kt-menu-overflow="true"
                    >
                        <i className="ki-solid ki-dots-vertical fs-1 text-gray-800"></i>
                    </Button>
                </Dropdown>
            ),
        },
    ];

    const menu = (row) => (
        <Menu className="min-w-200px p-3">
            <Menu.Item key="1" className="d-flex align-items-center mb-2">
                <i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Notification
            </Menu.Item>
            <Menu.Item key="2" className="d-flex align-items-center">
                <i className="ki-outline ki-trash fs-3 me-2"></i>Delete Notification
            </Menu.Item>
        </Menu>
    );

    const theme = getLocalAntdTheme(themeMode, "#f45b68");

    // CSS styles for mobile view
    const mobileStyles = `
        .alert-detail-modal .detail-section {
            margin-top: 20px;
        }
        
        .alert-detail-modal .detail-row {
            display: flex;
            padding: 12px 0;
            border-bottom: 1px solid #f0f0f0;
        }
        
        .alert-detail-modal .detail-row:last-child {
            border-bottom: none;
        }
        
        .alert-detail-modal .detail-label {
            width: 100px;
            font-weight: 500;
            color: #8c8c8c;
            flex-shrink: 0;
        }
        
        .alert-detail-modal .detail-value {
            flex: 1;
            color: #262626;
            word-break: break-word;
        }
        
        .alert-cards-container .text-one {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            white-space: normal;
        }
        
        @media (max-width: 768px) {
            .ant-card-body {
                padding: 12px;
            }
        }
    `;

    return (
        <Wrapper>
            <div>
                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Alerts</h3>
                                <div className="card-toolbar">
                                    <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                                        <li className="nav-item">
                                            <Link
                                                className={`nav-link ${activeTab === 'category' ? 'active' : ''}`}
                                                data-bs-toggle="tab"
                                                onClick={() => setActiveTab('category')}
                                            >
                                                Category
                                            </Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link
                                                className={`nav-link ${activeTab === 'type' ? 'active' : ''}`}
                                                data-bs-toggle="tab"
                                                onClick={() => setActiveTab('type')}
                                            >
                                                Type
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card-body pb-2" style={{ minHeight: 300 }}>
                                {activeTab === 'category' && (
                                    <Category
                                        userData={userData}
                                        analyticsData={analyticsData}
                                        loading={analyticsLoading}
                                        activeCategory={selectedCategory}
                                        onCategorySelect={onCategoryChange}
                                    />
                                )}
                                {console.log(analyticsData)}
                                {console.log(selectedCategory)}

                                {activeTab === 'type' && (
                                    <Type
                                        analyticsData={analyticsData}
                                        loading={analyticsLoading}
                                        onFilterChange={handleAlertTypeFilter}
                                        activeFilter={filter.alert_name}
                                    />
                                )}

                                <div className="tab-content" id="myTabContent">
                                    <div className="tab-pane fade show active" id="kt_tab_pane_1a" role="tabpanel"></div>
                                    <div className="tab-pane fade" id="kt_tab_pane_2a" role="tabpanel"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Alerts List</h3>
                                <div className="card-toolbar d-flex align-items-center gap-2">
                                    {selectedIds.length > 0 && (
                                        <button
                                            className="btn btn-danger fs-7 fw-bold"
                                            onClick={handleBulkMarkAsRead}
                                            disabled={markingRead}
                                        >
                                            {markingRead
                                                ? 'Marking...'
                                                : `Mark as Read${allSelected ? ' (All Selected)' : selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`
                                            }
                                        </button>
                                    )}
                                    <Select
                                        placeholder="Alert Status"
                                        style={{ width: 150, height: 35 }}
                                        value={filter.is_read !== null && filter.is_read !== undefined ? String(filter.is_read) : null}
                                        onChange={(val) => {
                                            const nextValue = (val === undefined || val === null) ? null : String(val);
                                            setFilter(prev => ({
                                                ...prev,
                                                is_read: nextValue,
                                                page: 1,
                                            }));
                                        }}
                                        className="me-2"
                                        allowClear
                                        onClear={() => setFilter(prev => ({ ...prev, is_read: null, page: 1 }))}
                                    >
                                        <Select.Option value="0">Unread</Select.Option>
                                        <Select.Option value="1">Read</Select.Option>
                                    </Select>
                                </div>
                            </div>
                            <div className="card-body py-3 px-3">
                                <div>
                                    {isMobile ? (
                                        // Mobile view - Card layout
                                        renderCardView()
                                    ) : (
                                        // Desktop/Tablet view - Table layout
                                        <Table
                                            scroll={{ x: 'max-content' }}
                                            sticky={{ offsetHeader: bannerHeight }}
                                            columns={columns}
                                            loading={loading}
                                            dataSource={list}
                                            rowKey="id"
                                            pagination={false}
                                            rowClassName={(record) => record.is_read ? READ_ROW_CLASS : ''}
                                            components={{
                                                table: (props) => (
                                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                                                ),
                                                header: {
                                                    wrapper: (props) => <thead {...props} className={props.className || ''} />,
                                                    row: (props) => <tr {...props} className={`${props.className || ''} fw-bolder text-dark bg-cus`} />,
                                                    cell: (props) => <th {...props} className={props.className || ''} />,
                                                },
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={theme}>
                                    <Pagination
                                        className="mt-3"
                                        current={filter.page}
                                        pageSize={filter['per-page']}
                                        total={totalPage}
                                        showSizeChanger
                                        onChange={(currentPage, newPageSize) => {
                                            setFilter(prevFilter => ({
                                                ...prevFilter,
                                                page: currentPage,
                                                'per-page': newPageSize,
                                            }));
                                        }}
                                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                        size={isMobile ? 'small' : 'default'}
                                        simple={isMobile}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Detail Modal for Mobile */}
            {renderDetailModal()}
            
            {/* Mobile Styles */}
            <style>{mobileStyles}</style>
        </Wrapper>
    );
}

export default AllAlerts;